import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type CreditEvent = {
  id: string;
  delta: number;
  reason: string;
  ref: string | null;
  created_at: string;
};

/** The signed-in user's blueprint-download credit balance and ledger. */
export function useBlueprintCredits(userId: string | undefined) {
  const [balance, setBalance] = useState<number | null>(null);
  const [lifetimeEarned, setLifetimeEarned] = useState(0);
  const [events, setEvents] = useState<CreditEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!userId) {
      setBalance(null);
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [{ data: credits }, { data: ledger }] = await Promise.all([
      supabase
        .from("blueprint_credits")
        .select("balance,lifetime_earned")
        .eq("user_id", userId)
        .maybeSingle(),
      supabase
        .from("blueprint_credit_events")
        .select("id,delta,reason,ref,created_at")
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    setBalance(credits?.balance ?? 0);
    setLifetimeEarned(credits?.lifetime_earned ?? 0);
    setEvents((ledger as CreditEvent[]) ?? []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refetch();
    if (!userId) return;
    const channel = supabase
      .channel(`blueprint-credits:${userId}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "blueprint_credits", filter: `user_id=eq.${userId}` },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refetch]);

  return { balance, lifetimeEarned, events, loading, refetch };
}
